/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEqual from '../tools/isEqual.js';

var Selection = function Selection(_ref) {
  var children = _ref.children,
    initialSelectedItems = _ref.initialSelectedItems,
    mode = _ref.mode,
    onChange = _ref.onChange,
    render = _ref.render;
  var _useState = useState(initialSelectedItems),
    _useState2 = _slicedToArray(_useState, 2),
    selectedItems = _useState2[0],
    setSelectedItems = _useState2[1];
  var isFirstRender = useRef(true);
  useEffect(function () {
    if (!isFirstRender.current && onChange) {
      onChange({
        selectedItems: selectedItems
      });
    }
  }, [onChange, selectedItems]);
  useEffect(function () {
    isFirstRender.current = false; // toggle flag after first render/mounting
  }, []);
  var handleClearSelection = useCallback(function () {
    setSelectedItems([]);
  }, []);
  var handleSelectItem = useCallback(function (item) {
    var items = Array.isArray(item) ? item : [item];
    setSelectedItems(function (prevState) {
      return [].concat(_toConsumableArray(prevState), _toConsumableArray(items));
    });
  }, []);
  var handleRemoveItem = useCallback(function (item) {
    var items = Array.isArray(item) ? item : [item];
    setSelectedItems(function (prevState) {
      return prevState.filter(function (itemOnState) {
        return items.indexOf(itemOnState) === -1;
      });
    });
  }, []);
  var handleOnItemChange = function handleOnItemChange(item) {
    var itemsToProcess = Array.isArray(item) ? item : [item];
    if (mode === 'single') {
      if (itemsToProcess[0] !== selectedItems[0]) {
        setSelectedItems(itemsToProcess);
      }
    } else {
      var result = itemsToProcess.reduce(function (acc, theItem) {
        var selectedIndex;
        selectedItems.some(function (selectedItem, index) {
          if (isEqual(selectedItem, theItem)) {
            selectedIndex = index;
            return true;
          }
          return false;
        });
        if (selectedIndex === undefined) {
          acc.itemsToSelect.push(theItem);
        } else {
          acc.itemsToRemove.push(selectedItems[selectedIndex]);
        }
        return acc;
      }, {
        itemsToSelect: [],
        itemsToRemove: []
      });
      if (result.itemsToSelect.length > 0) {
        handleSelectItem(result.itemsToSelect);
      }
      if (result.itemsToRemove.length > 0) {
        handleRemoveItem(result.itemsToRemove);
      }
    }
  };
  var renderProps = {
    selectedItems: selectedItems,
    onItemChange: handleOnItemChange,
    clearSelection: handleClearSelection
  };
  if (render !== undefined) {
    return render(renderProps);
  }
  if (children !== undefined) {
    return children(renderProps);
  }
  return null;
};
Selection.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  initialSelectedItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func,
  mode: PropTypes.oneOf(['single', 'multi']),
  render: PropTypes.func
};
Selection.defaultProps = {
  mode: 'multi',
  render: undefined,
  onChange: undefined,
  children: undefined
};
var Selection$1 = Selection;

export { Selection$1 as default };
